import { useCallback, useEffect, useRef, useState } from 'react';

import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';

import { BrowserMultiFormatReader } from '@zxing/browser';
import { BarcodeFormat, DecodeHintType } from '@zxing/library';

const MOBILE_MEDIA_QUERY = '(max-width: 900px)';

function normalizarCodigo(codigo) {
    return String(codigo || '')
        .trim()
        .replace(/[\r\n]/g, '');
}

function mensagemErroCamera(error) {
    const nome = error?.name || error?.constructor?.name || 'Erro desconhecido';

    switch (nome) {
        case 'NotAllowedError':
        case 'PermissionDeniedError':
            return 'A permissão da câmera foi negada. No Chrome, abra as permissões do site e permita o uso da câmera.';

        case 'NotFoundError':
        case 'DevicesNotFoundError':
            return 'Nenhuma câmera foi encontrada neste dispositivo.';

        case 'NotReadableError':
        case 'TrackStartError':
            return 'A câmera está sendo utilizada por outro aplicativo. Feche os outros aplicativos e tente novamente.';

        case 'OverconstrainedError':
        case 'ConstraintNotSatisfiedError':
            return 'A câmera não aceitou a configuração solicitada.';

        case 'SecurityError':
            return 'O navegador bloqueou a câmera. Verifique se o sistema está sendo acessado por HTTPS.';

        case 'AbortError':
            return 'O navegador interrompeu a inicialização da câmera. Tente novamente.';

        case 'ChunkLoadError':
            return 'Não foi possível carregar o leitor. Atualize a página e tente novamente.';
        case 'CameraTimeoutError':
            return 'A câmera não respondeu em 12 segundos. Feche outros aplicativos que estejam usando a câmera e tente novamente.';

        case 'VideoPlaybackError':
            return 'A câmera foi encontrada, mas o navegador não conseguiu apresentar a imagem.';
        default:
            return `Não foi possível iniciar a câmera. Detalhes: ${nome}${error?.message ? ` - ${error.message}` : ''}`;
    }
}

function erroNormalDeLeitura(error) {
    const nome = error?.name || error?.constructor?.name;

    return ['NotFoundException', 'ChecksumException', 'FormatException'].includes(nome);
}

function aguardarComTimeout(promise, tempo, nome, mensagem) {
    let timeoutId;

    const timeoutPromise = new Promise((resolve, reject) => {
        timeoutId = window.setTimeout(() => {
            reject(
                Object.assign(new Error(mensagem), {
                    name: nome,
                })
            );
        }, tempo);
    });

    return Promise.race([promise, timeoutPromise]).finally(() => {
        window.clearTimeout(timeoutId);
    });
}

function BarcodeScannerButton({ name, handleOnChange, disabled = false, buttonText = 'Ler código', iconOnly = false }) {
    const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);

    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [videoPronto, setVideoPronto] = useState(false);

    const videoRef = useRef(null);
    const controlsRef = useRef(null);
    const streamRef = useRef(null);
    const audioContextRef = useRef(null);
    const leituraConcluidaRef = useRef(false);

    const handleOnChangeRef = useRef(handleOnChange);
    const nameRef = useRef(name);

    handleOnChangeRef.current = handleOnChange;
    nameRef.current = name;

    const cameraDisponivel =
        typeof window !== 'undefined' &&
        window.isSecureContext === true &&
        typeof window.navigator?.mediaDevices?.getUserMedia === 'function';

    const prepararAudio = useCallback(() => {
        try {
            const AudioContext = window.AudioContext || window.webkitAudioContext;

            if (!AudioContext) return;

            if (!audioContextRef.current) {
                audioContextRef.current = new AudioContext();
            }

            if (audioContextRef.current.state === 'suspended') {
                audioContextRef.current.resume().catch(() => {});
            }
        } catch {
            // A leitura continua mesmo se o navegador não permitir o som.
        }
    }, []);

    const emitirConfirmacao = useCallback(() => {
        try {
            const audioContext = audioContextRef.current;

            if (audioContext) {
                if (audioContext.state === 'suspended') {
                    audioContext.resume().catch(() => {});
                }

                const inicio = audioContext.currentTime;
                const oscillator = audioContext.createOscillator();
                const gain = audioContext.createGain();

                oscillator.type = 'sine';
                oscillator.frequency.setValueAtTime(1046, inicio);

                gain.gain.setValueAtTime(0.0001, inicio);
                gain.gain.exponentialRampToValueAtTime(0.25, inicio + 0.015);
                gain.gain.exponentialRampToValueAtTime(0.0001, inicio + 0.16);

                oscillator.connect(gain);
                gain.connect(audioContext.destination);

                oscillator.start(inicio);
                oscillator.stop(inicio + 0.17);
            }
        } catch {
            // O preenchimento do campo não depende do som.
        }

        try {
            window.navigator?.vibrate?.(90);
        } catch {
            // Alguns navegadores não oferecem vibração.
        }
    }, []);

    const pararCamera = useCallback(() => {
        try {
            controlsRef.current?.stop();
        } catch {
            // O leitor pode já ter sido encerrado.
        }

        controlsRef.current = null;

        const stream = streamRef.current || videoRef.current?.srcObject;

        if (stream?.getTracks) {
            stream.getTracks().forEach((track) => track.stop());
        }

        streamRef.current = null;

        if (videoRef.current) {
            videoRef.current.pause();
            videoRef.current.srcObject = null;
        }
    }, []);

    const fecharScanner = useCallback(() => {
        leituraConcluidaRef.current = true;
        pararCamera();
        setOpen(false);
        setLoading(false);
    }, [pararCamera]);

    const registrarVideo = useCallback((element) => {
        videoRef.current = element;
        setVideoPronto(Boolean(element));
    }, []);

    function abrirScanner() {
        if (disabled) return;

        prepararAudio();
        leituraConcluidaRef.current = false;
        setError('');
        setOpen(true);
    }

    useEffect(() => {
        if (!open || !videoPronto) return undefined;

        let effectAtivo = true;

        async function iniciarScanner() {
            setLoading(true);
            setError('');

            try {
                if (!cameraDisponivel) {
                    throw Object.assign(new Error('Contexto não seguro'), {
                        name: 'SecurityError',
                    });
                }

                if (!effectAtivo || !videoRef.current) return;

                const hints = new Map();

                hints.set(DecodeHintType.POSSIBLE_FORMATS, [
                    BarcodeFormat.EAN_13,
                    BarcodeFormat.EAN_8,
                    BarcodeFormat.UPC_A,
                    BarcodeFormat.UPC_E,
                    BarcodeFormat.CODE_128,
                    BarcodeFormat.ITF,
                    BarcodeFormat.QR_CODE,
                ]);

                hints.set(DecodeHintType.TRY_HARDER, true);
                // Código de barras de boleto bancário possui 44 dígitos.
                hints.set(DecodeHintType.ALLOWED_LENGTHS, [44]);

                const codeReader = new BrowserMultiFormatReader(hints, {
                    delayBetweenScanAttempts: 100,
                    delayBetweenScanSuccess: 500,
                });

                const tratarLeitura = (result, leituraError, scannerControls) => {
                    if (result && !leituraConcluidaRef.current) {
                        const codigo = normalizarCodigo(result.getText());

                        if (!codigo) return;

                        leituraConcluidaRef.current = true;

                        try {
                            scannerControls?.stop();
                        } catch {
                            // O fechamento normal encerrará o leitor.
                        }

                        emitirConfirmacao();

                        handleOnChangeRef.current?.({
                            target: {
                                name: nameRef.current,
                                value: codigo,
                            },
                        });

                        fecharScanner();
                        return;
                    }

                    if (leituraError && !erroNormalDeLeitura(leituraError)) {
                        console.error('Erro durante a leitura:', leituraError);
                    }
                };

                const cameraPromise = window.navigator.mediaDevices.getUserMedia({
                    audio: false,
                    video: {
                        facingMode: {
                            ideal: 'environment',
                        },
                        width: {
                            ideal: 1920,
                        },
                        height: {
                            ideal: 1080,
                        },
                        aspectRatio: {
                            ideal: 16 / 9,
                        },
                        advanced: [
                            {
                                focusMode: 'continuous',
                            },
                        ],
                    },
                });

                let stream;

                try {
                    stream = await aguardarComTimeout(
                        cameraPromise,
                        12000,
                        'CameraTimeoutError',
                        'Tempo excedido ao abrir a câmera.'
                    );
                } catch (cameraError) {
                    if (cameraError?.name === 'CameraTimeoutError') {
                        cameraPromise
                            .then((streamAtrasado) => {
                                streamAtrasado.getTracks().forEach((track) => track.stop());
                            })
                            .catch(() => {});
                    }

                    throw cameraError;
                }

                if (!effectAtivo || !videoRef.current) {
                    stream.getTracks().forEach((track) => track.stop());
                    return;
                }

                streamRef.current = stream;

                const video = videoRef.current;

                video.srcObject = stream;
                video.muted = true;
                video.playsInline = true;

                await aguardarComTimeout(video.play(), 8000, 'VideoPlaybackError', 'Tempo excedido ao reproduzir o vídeo.');

                if (!effectAtivo || leituraConcluidaRef.current) {
                    stream.getTracks().forEach((track) => track.stop());
                    return;
                }

                // A imagem já está sendo apresentada.
                // O spinner pode ser retirado antes de iniciar o ZXing.
                setLoading(false);

                const controls = await codeReader.decodeFromVideoElement(video, tratarLeitura);

                if (!effectAtivo || leituraConcluidaRef.current) {
                    controls.stop();
                    return;
                }

                controlsRef.current = controls;
                setLoading(false);
            } catch (cameraError) {
                if (!effectAtivo) return;

                console.error('Erro ao iniciar leitor:', cameraError);
                pararCamera();
                setLoading(false);
                setError(mensagemErroCamera(cameraError));
            }
        }

        iniciarScanner();

        return () => {
            effectAtivo = false;
            leituraConcluidaRef.current = true;
            pararCamera();
        };
    }, [cameraDisponivel, emitirConfirmacao, fecharScanner, open, pararCamera, videoPronto]);

    useEffect(() => {
        return () => {
            try {
                audioContextRef.current?.close();
            } catch {
                // O contexto de áudio pode já estar fechado.
            }
        };
    }, []);

    if (!isMobile) return null;

    const tooltip = cameraDisponivel ? 'Ler código com a câmera' : 'A câmera requer navegador compatível e conexão HTTPS';

    const scannerDialog = (
        <Dialog open={open} onClose={fecharScanner} fullScreen={isMobile} fullWidth maxWidth="sm">
            <DialogTitle>Ler código de barras ou QR Code</DialogTitle>

            <DialogContent>
                <Stack spacing={2}>
                    <Typography variant="body2">
                        Centralize o código na área marcada. Para boletos, vire o celular de lado e enquadre todas as barras.
                    </Typography>

                    {error && <Alert severity="error">{error}</Alert>}

                    <div
                        style={{
                            position: 'relative',
                            minHeight: '300px',
                            overflow: 'hidden',
                            borderRadius: '6px',
                            backgroundColor: '#000',
                        }}
                    >
                        <video
                            ref={registrarVideo}
                            autoPlay
                            muted
                            playsInline
                            style={{
                                display: 'block',
                                width: '100%',
                                minHeight: '300px',
                                maxHeight: '72vh',
                                objectFit: 'contain',
                                aspectRatio: '16 / 9',
                                backgroundColor: '#000',
                            }}
                        />

                        <div
                            style={{
                                position: 'absolute',
                                top: '22%',
                                right: '6%',
                                bottom: '22%',
                                left: '6%',
                                border: '2px solid #ce33ff',
                                borderRadius: '8px',
                                pointerEvents: 'none',
                            }}
                        />

                        {loading && (
                            <div
                                style={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'rgba(0, 0, 0, 0.45)',
                                }}
                            >
                                <CircularProgress />
                            </div>
                        )}
                    </div>

                    {!loading && !error && (
                        <Typography variant="caption" textAlign="center">
                            Procurando código...
                        </Typography>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button type="button" onClick={fecharScanner}>
                    Cancelar
                </Button>
            </DialogActions>
        </Dialog>
    );

    if (iconOnly) {
        return (
            <>
                <Tooltip title={tooltip}>
                    <span>
                        <IconButton
                            type="button"
                            size="small"
                            aria-label={buttonText}
                            onClick={abrirScanner}
                            disabled={disabled || !cameraDisponivel}
                            sx={{
                                width: 32,
                                height: 32,
                                color: 'text.secondary',
                                backgroundColor: 'transparent',
                                '&:hover': {
                                    color: 'primary.main',
                                    backgroundColor: 'action.hover',
                                },
                            }}
                        >
                            <QrCodeScannerIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>

                {scannerDialog}
            </>
        );
    }

    return (
        <>
            <div
                style={{
                    display: 'inline-flex',
                    alignItems: 'flex-end',
                    marginLeft: '8px',
                }}
            >
                <Tooltip title={tooltip}>
                    <span>
                        <Button
                            type="button"
                            variant="outlined"
                            onClick={abrirScanner}
                            disabled={disabled || !cameraDisponivel}
                            sx={{
                                height: '37px',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {buttonText}
                        </Button>
                    </span>
                </Tooltip>
            </div>

            {scannerDialog}
        </>
    );
}

export default BarcodeScannerButton;
